/**
 * ADO.NET / SqlClient connection-string parser → mssql.
 *
 * Example:
 *   Server=tcp:myapp.database.windows.net,1433;Database=prod;User Id=admin;
 *   Password=…;Encrypt=True;TrustServerCertificate=False
 */

import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import type { ParserMatch, ConnectionStringParser } from './types';
import { applyMssqlTls, isAzureSqlHost, noteMssqlTls, type MssqlTlsValues } from './helpers';
import { scanDotNetConnectionString, type KeyValuePair } from '@/core/utils/connectionStringTokens';
import { effectivePairs } from './repeatedKeywords';

const SERVER_KEYS = ['server', 'data source', 'address', 'addr', 'network address'];

/** Parse an ADO.NET server value: strip `tcp:`, split `host,port`, keep instance. */
function parseServer(raw: string): { server: string; port?: string } {
  let value = raw.trim();
  // Strip a leading protocol prefix (tcp:, np:, lpc:).
  value = value.replace(/^(tcp|np|lpc):/i, '');
  // Named instance (host\SQLEXPRESS): keep whole value, port stays auto.
  if (value.includes('\\') && !value.includes(',')) {
    return { server: value };
  }
  // Comma port form: host,1433
  const commaIdx = value.lastIndexOf(',');
  if (commaIdx !== -1) {
    const host = value.slice(0, commaIdx).trim();
    const port = value.slice(commaIdx + 1).trim();
    if (/^\d+$/.test(port)) {
      return { server: host, port };
    }
  }
  return { server: value };
}

/**
 * ADO.NET / SqlClient and ODBC → mssql. The pairs come from .NET's own connection-string parser, with SqlClient rules
 * (`"…"` / `'…'`) or ODBC rules (`{…}`) — `scanDotNetConnectionString` (C3 reviews C3-01, r5 C3-21); `null` input is a
 * string that parser rejects.
 */
export const parseAdoNet: ConnectionStringParser = (input) => {
  const trimmed = input.trim();
  // A `://` scheme or `jdbc:` prefix is not ADO.NET (those are handled by URL/JDBC parsers).
  if (/^[a-z]+:\/\//i.test(trimmed) || /^jdbc:/i.test(trimmed)) return null;
  const pairs = scanDotNetConnectionString(trimmed, 'sqlclient');
  return pairs ? adoNetMatch(pairs, 'sqlclient') : null;
};

/**
 * The fields of a SqlClient (`'sqlclient'`) or ODBC (`'odbc'`) string's pairs. A repeated keyword or synonym is read by
 * that driver's rule first (`repeatedKeywords.ts`: SqlClient the last, ODBC the first occurrence), so each keyword below
 * is seen once — `Integrated Security=True;Integrated Security=False` is SQL Server authentication.
 */
export function adoNetMatch(scanned: readonly KeyValuePair[], format: 'sqlclient' | 'odbc'): ParserMatch | null {
  const pairs = effectivePairs(scanned, format);
  const values: Partial<ConnectionFormValues> = {};
  const matchedFields: string[] = [];
  const unmatchedKeys: string[] = [];
  const tls: MssqlTlsValues = {};
  let recognized = false;

  for (const pair of pairs) {
    const key = pair.key.toLowerCase();
    const rawValue = pair.value;

    if (SERVER_KEYS.includes(key)) {
      recognized = true;
      const { server, port } = parseServer(rawValue);
      values.server = server;
      matchedFields.push('server');
      if (port) {
        values.port = port;
        matchedFields.push('port');
      }
    } else if (key === 'database' || key === 'initial catalog') {
      recognized = true;
      values.database = rawValue;
      matchedFields.push('database');
    } else if (key === 'user id' || key === 'uid' || key === 'user') {
      recognized = true;
      values.login = rawValue;
      matchedFields.push('user');
    } else if (key === 'password' || key === 'pwd') {
      recognized = true;
      values.password = rawValue;
      matchedFields.push('password');
    } else if (key === 'encrypt') {
      recognized = true;
      noteMssqlTls(tls, 'encrypt', rawValue);
    } else if (key === 'trustservercertificate' || key === 'trust server certificate') {
      recognized = true;
      noteMssqlTls(tls, 'trust', rawValue);
    } else if (key === 'integrated security' || key === 'trusted_connection') {
      recognized = true;
      const v = rawValue.trim().toLowerCase();
      if (v === 'true' || v === 'sspi' || v === 'yes') {
        values.authentication = 'Windows authentication';
        matchedFields.push('authentication');
      }
    } else {
      // Unrecognised key — record the ORIGINAL-case key name for the UI.
      unmatchedKeys.push(pair.key);
    }
  }

  if (!recognized) return null;
  // Encrypt=Strict validates the certificate: Trust server certificate off, whatever the string says (mssqlEncrypt.ts).
  // A value the drivers reject fills neither and refuses the string (`tlsUnreadable`, review round 5).
  const { strict: encryptStrict, unreadable } = applyMssqlTls(tls, values, matchedFields);

  const suggestEntra =
    isAzureSqlHost(values.server) &&
    !values.password &&
    values.authentication !== 'Windows authentication';

  const match: ParserMatch = {
    format: 'adonet',
    engine: 'mssql',
    values,
    matchedFields,
    unmatchedKeys,
  };
  if (suggestEntra) match.suggestEntra = true;
  if (encryptStrict) match.encryptStrict = true;
  if (unreadable) match.tlsUnreadable = unreadable;
  return match;
}
